import React, { useMemo, useState } from 'react'

type Question = {
  id: string
  title: string
  note?: string
  multi?: boolean
  options: string[]
}

const QUESTIONS: Question[] = [
  { id: 'goal', title: 'What’s your primary goal?', options: ['Ship faster', 'Reduce costs', 'Scale capabilities', 'Prototype agents'] },
  { id: 'stage', title: 'Where is your product today?', options: ['Pre‑product', 'MVP in market', 'Growing users', 'Enterprise scale'] },
  { id: 'stack', title: 'Primary stack environment?', options: ['AWS', 'GCP', 'Azure', 'On‑prem / Hybrid'] },
  { id: 'surface', title: 'Where should agents act?', multi: true, options: ['Code & CI/CD', 'Dashboards / Backoffice', 'APIs / Integrations', 'Data / ML'] },
  { id: 'guardrails', title: 'Guardrail preference?', options: ['Strict approvals', 'Light approvals', 'Autonomy with audit'] },
  { id: 'data', title: 'Data access readiness?', options: ['Clean & documented', 'Partial / WIP', 'Needs work'] },
  { id: 'security', title: 'Compliance sensitivity?', options: ['High (SOC2/ISO/HIPAA)', 'Moderate', 'Low'] },
  { id: 'timeline', title: 'Timeline?', options: ['ASAP', '2–4 weeks', '1–3 months', 'Exploring'] },
  { id: 'budget', title: 'Budget band?', options: ['<$10k', '$10–30k', '$30–100k', '$100k+'] },
  { id: 'integration', title: 'Key integrations?', multi: true, options: ['GitHub/GitLab', 'Slack', 'Jira', 'Snowflake/DBT', 'GCS/S3', 'Zapier/Workato'] },
  { id: 'success', title: 'How do you measure success?', options: ['Feature throughput', 'Latency/quality gains', 'Cost reduction', 'Reliability'] },
  { id: 'ownership', title: 'Engagement style?', options: ['We drive; you review', 'Shared squad', 'You drive; we augment'] },
  { id: 'scale', title: 'Scale intent?', options: ['Pilot', 'Team rollout', 'Org‑wide', 'Unsure'] },
  { id: 'fit', title: 'Does “Agent Factory” resonate for you?', options: ['Yes', 'Mostly', 'Curious', 'Not sure'] },
]

export default function FitQuiz({ onClose, onStartProject }: { onClose: () => void; onStartProject: () => void }) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, string[]>>({})

  const total = QUESTIONS.length
  const q = QUESTIONS[step]

  function toggle(option: string) {
    const cur = answers[q.id] || []
    let next: string[]
    if (q.multi) {
      next = cur.includes(option) ? cur.filter(o => o !== option) : [...cur, option]
    } else {
      next = [option]
    }
    setAnswers({ ...answers, [q.id]: next })
    if (!q.multi) setTimeout(() => setStep(s => Math.min(s + 1, total - 1)), 0)
  }

  function back() { setStep(s => Math.max(s - 1, 0)) }
  function next() { if ((answers[q.id] || []).length > 0) setStep(s => Math.min(s + 1, total - 1)) }

  const pct = useMemo(() => Math.round(((step + 1) / total) * 100), [step, total])

  const summary = useMemo(() => {
    const lines: string[] = []
    QUESTIONS.forEach(qq => {
      const sel = answers[qq.id]
      if (sel && sel.length) lines.push(`${qq.title}: ${sel.join(', ')}`)
    })
    return lines.join('\n')
  }, [answers])

  const done = step === total - 1 && (answers[q.id] || []).length > 0

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__card" style={{ maxWidth: 720 }}>
        <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        <div className="progress">
          <div className="progress__bar" style={{ width: `${pct}%` }} />
          <div className="progress__label">{pct}%</div>
        </div>
        <div>
          <h2>{q.title}</h2>
          {q.note && <p className="muted">{q.note}</p>}
          <div className={`choices ${q.multi ? 'grid' : ''}`}>
            {q.options.map(o => {
              const on = (answers[q.id] || []).includes(o)
              return (
                <label key={o} className={`choice ${on ? 'selected' : ''}`}>
                  <input type={q.multi ? 'checkbox' : 'radio'} checked={on} onChange={() => toggle(o)} />
                  <span>{o}</span>
                </label>
              )
            })}
          </div>
        </div>
        <div className="modal__actions">
          {step > 0 ? <button className="btn ghost" onClick={back}>Back</button> : <span />}
          {done ? (
            <button className="btn primary" onClick={onStartProject}>Start a project</button>
          ) : (
            q.multi ? <button className="btn primary" onClick={next} disabled={(answers[q.id]||[]).length===0}>Next</button> : <button className="btn primary" onClick={next}>Next</button>
          )}
        </div>
        {done && (
          <div className="row">
            <pre className="summary" aria-label="Fit summary">{summary}</pre>
          </div>
        )}
      </div>
    </div>
  )
}

